<template>
  <a-modal
    :visible="visible"
    width="80%"
    @cancel="handleClose"
  >
    <div>
      <div class="cropper-container">
        <VueCropper
          v-if="croppedImage?.preview"
          ref="cropperRef"
          :src="croppedImage.preview"
          :viewMode="1"
          :initialAspectRatio="1"
          background
          rotatable
          :imgStyle="{ width: '100%' }"
          class="cropper-container__item"
          preview=".cropper-container__item--preview"
          @crop="calculateSizes"
        />

        <a-button
          shape="circle"
          type="primary"
          icon="redo"
          class="rotate-button"
          @click="rotate"
        />

        <div class="cropper-container__item">
          <div class="cropper-container__item--preview" />
        </div>

        <div
          v-if="imageSize.width || imageSize.height"
          class="cropper-resolution"
          :class="{ 'cropper-resolution--error': disableCrop }"
        >
          {{ imageSize.width }}x{{ imageSize.height }}
        </div>
      </div>
    </div>

    <template #footer>
      <a-button
        type="primary"
        :disabled="disableCrop"
        :loading="isPending"
        @click="onSubmit"
      >
        {{ $t("common.save") }}
      </a-button>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import "cropperjs/dist/cropper.css"

import { message } from "ant-design-vue"
import { computed, onMounted, reactive, ref, watch } from "vue"
import VueCropper from "vue-cropperjs"

type CroppedImageDetails = { detail: Record<string, number> }
type Props = {
  visible: boolean
  croppedImage?: Record<string, string | File>
}
type Emits = {
  (event: "onCropped", image: Blob): void
  (event: "close"): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const cropperRef = ref<typeof VueCropper>()
const isPending = ref<boolean>(false)
const imageSize = reactive({
  width: 0,
  height: 0
})

const disableCrop = computed(() => imageSize.width < 500 || imageSize.height < 500)

const calculateSizes = (imageInfo: CroppedImageDetails) => {
  imageSize.width = Math.round(imageInfo.detail.width)
  imageSize.height = Math.round(imageInfo.detail.height)
}
const rotate = () => {
  cropperRef.value.rotate(90)
}
const initCropper = async () => {
  if (!cropperRef.value) return

  if (!props.visible) {
    cropperRef.value.replace(undefined)
    return
  }

  try {
    const response = await fetch(props.croppedImage.preview as string, {
      method: "GET",
      mode: "cors"
    })

    const reader = new FileReader()
    reader.onload = (e) => {
      if (cropperRef.value) {
        cropperRef.value.replace(e.target!.result)
      }
    }
    reader.readAsDataURL(await response.blob())
  } catch (error) {
    message.error(error)
  }
}
const onSubmit = async () => {
  isPending.value = true

  cropperRef.value.getCroppedCanvas().toBlob(async (blob: Blob) => {
    isPending.value = false

    const imageFormat = props.croppedImage.format

    emit(
      "onCropped",
      new File([blob], `${self.crypto.randomUUID()}.${imageFormat}`, {
        type: `image/${imageFormat}`
      })
    )
  })
}
const handleClose = () => {
  emit("close")
}

watch(() => props.visible, initCropper)

onMounted(initCropper)
</script>

<style lang="scss" scoped>
.cropper-header {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  margin: 24px 0;
}

.cropper-container {
  display: flex;
  flex-flow: row nowrap;
  gap: 32px;

  &__item {
    width: calc(50% - 16px);
    height: 60dvh;

    display: flex;
    justify-content: center;
    align-items: center;

    &--preview {
      overflow: hidden;
      width: 100%;
      height: 100%;
    }
  }

  & > .rotate-button {
    position: absolute;
    left: 25%;
    translate: -24px 16px;
  }

  & > .cropper-resolution {
    position: absolute;
    bottom: 16px;
    right: 20%;
    translate: 50% 0;
    color: white;
    font-size: 16px;
    line-height: 32px;
    padding: 4px 12px;
    background-color: rgba(22, 22, 22, 0.5);
    box-shadow: 0px 4px 10px rgba(22, 22, 22, 0.5);
    border-radius: 8px;

    &--error {
      background-color: rgba(224, 29, 29, 1);
    }
  }
}
</style>
